import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Button from "../shared/button/Button";
import { trackLead } from "../analytics/track";

const countryCodes = [
  { code: "+1", country: "USA" },
  { code: "+44", country: "GBR" },
  { code: "+91", country: "IND" },
  { code: "+61", country: "AUS" },
  { code: "+49", country: "DEU" },
  { code: "+33", country: "FRA" },
  { code: "+81", country: "JPN" },
  { code: "Other", country: "" },
];

const companySizes = [
  "1-10 employees",
  "11-50 employees",
  "51-200 employees",
  "201-500 employees",
  "501-1000 employees",
  "1001+ employees",
];

type FormData = {
  name: string;
  email: string;
  countryCode: string;
  phone: string;
  companyName: string;
  companySize: string;
  comment: string;
};

type Errors = Partial<Record<keyof FormData, string>>;

const inputClass =
  "font-manrope rounded-lg border bg-gray-900 px-4 py-3 text-gray-50 placeholder-gray-500 transition-all focus:outline-none focus:ring-2 focus:ring-teal-500/20";

const borderClass = (hasError?: string) =>
  hasError ? "border-red-500 focus:border-red-500" : "border-gray-700 focus:border-teal-500";

export default function Contactus() {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    countryCode: "+91",
    phone: "",
    companyName: "",
    companySize: "",
    comment: "",
  });
  const [formFilled, setFormFilled] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  const validateEmail = (email: string): string => {
    const personalDomains = ["gmail.com", "yahoo.com", "hotmail.com"];
    const domain = email.split("@")[1]?.toLowerCase();

    if (!email) return "Email is required";
    if (!/\S+@\S+\.\S+/.test(email)) return "Invalid email format";
    if (domain && personalDomains.includes(domain)) return "Use work email";
    return "";
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name as keyof Errors]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (name === "email") {
      const emailError = validateEmail(value);
      if (emailError) setErrors((prev) => ({ ...prev, email: emailError }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newErrors: Errors = {};
    if (!formData.name) newErrors.name = "Name is required";
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;
    if (!formData.phone) newErrors.phone = "Phone is required";
    if (!formData.companyName) newErrors.companyName = "Company name required";
    if (!formData.companySize) newErrors.companySize = "Company size required";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_ACCESS_KEY,
          subject: "New Contact Us Submission — Babblebots Website",
          ...formData,
          phone: `${formData.countryCode} ${formData.phone}`,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to submit form");
      }

      setFormFilled(true);
      trackLead("contact_us", "Contact Us");
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Failed to submit the form. Please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Babblebots AI</title>
      </Helmet>

      <div className="min-h-screen bg-gray-950 text-gray-50">
        <section className="relative overflow-hidden border-b border-gray-800 bg-gradient-to-b from-gray-900 to-gray-950 px-6 pt-14 pb-6 lg:pt-16 lg:pb-8">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-900/20 via-gray-950 to-gray-950" />
          <div className="relative mx-auto max-w-2xl text-center">
            <h1 className="font-satoshi mb-4 text-4xl font-bold tracking-tight sm:text-5xl">
              Contact Us
            </h1>
            <p className="font-manrope text-lg text-gray-300">
              Tell us what's slowing down your hiring. We'll help you fix it.
            </p>
          </div>
        </section>

        <section className="px-6 pt-6 pb-16 lg:pt-8 lg:pb-20">
          <div className="mx-auto max-w-2xl">
            {formFilled ? (
              <div className="rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900 to-gray-950 p-10 text-center">
                <p className="font-manrope text-lg text-gray-200">
                  Thank you for filling the form — we'll reach out to you
                  soon.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="space-y-6 rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900 to-gray-950 p-6 sm:p-8"
              >
                <div>
                  <input
                    id="name"
                    name="name"
                    aria-label="Name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`${inputClass} ${borderClass(errors.name)} w-full`}
                    placeholder="Name"
                  />
                  {errors.name && (
                    <p className="mt-1.5 text-sm text-red-400">{errors.name}</p>
                  )}
                </div>

                <div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    aria-label="Work Email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`${inputClass} ${borderClass(errors.email)} w-full`}
                    placeholder="Work Email *"
                  />
                  {errors.email && (
                    <p className="mt-1.5 text-sm text-red-400">{errors.email}</p>
                  )}
                </div>

                <div>
                  <div className="flex gap-2">
                    <select
                      name="countryCode"
                      aria-label="Country code"
                      value={formData.countryCode}
                      onChange={handleChange}
                      className={`${inputClass} ${borderClass()} w-[7.5rem] flex-shrink-0 sm:w-36`}
                    >
                      {countryCodes.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.country ? `${item.code} (${item.country})` : item.code}
                        </option>
                      ))}
                    </select>
                    <input
                      id="phone"
                      name="phone"
                      aria-label="Phone Number"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`${inputClass} ${borderClass(errors.phone)} min-w-0 flex-1`}
                      placeholder={
                        formData.countryCode === "Other"
                          ? "Number with country code"
                          : "Phone Number"
                      }
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-1.5 text-sm text-red-400">{errors.phone}</p>
                  )}
                </div>

                <div>
                  <input
                    id="companyName"
                    name="companyName"
                    aria-label="Company Name"
                    value={formData.companyName}
                    onChange={handleChange}
                    className={`${inputClass} ${borderClass(errors.companyName)} w-full`}
                    placeholder="Company Name *"
                  />
                  {errors.companyName && (
                    <p className="mt-1.5 text-sm text-red-400">
                      {errors.companyName}
                    </p>
                  )}
                </div>

                <div>
                  <select
                    id="companySize"
                    name="companySize"
                    aria-label="Company Size"
                    value={formData.companySize}
                    onChange={handleChange}
                    className={`${inputClass} ${borderClass(errors.companySize)} w-full ${
                      formData.companySize ? "text-gray-50" : "text-gray-500"
                    }`}
                  >
                    <option value="" disabled>
                      Company Size *
                    </option>
                    {companySizes.map((size) => (
                      <option key={size} value={size} className="text-gray-50">
                        {size}
                      </option>
                    ))}
                  </select>
                  {errors.companySize && (
                    <p className="mt-1.5 text-sm text-red-400">
                      {errors.companySize}
                    </p>
                  )}
                </div>

                <div>
                  <textarea
                    id="comment"
                    name="comment"
                    aria-label="How can Babblebots help you?"
                    rows={4}
                    value={formData.comment}
                    onChange={handleChange}
                    className={`${inputClass} ${borderClass()} w-full`}
                    placeholder="How can Babblebots help you?"
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  disabled={submitting}
                >
                  {submitting ? "Sending..." : "Submit"}
                </Button>
              </form>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
